import { useTable, List, ShowButton } from "@refinedev/antd";
import { Table, Space, Button, Input, Tag } from "antd";
import type { TableProps } from "antd";
import { ClearOutlined, SearchOutlined, PictureOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { ControlPoint, ControlPointStatus } from "../../types";
import { PhotoGalleryModal } from "../../components/PhotoGalleryModal";
import dayjs from "dayjs";
import { useState, useMemo } from "react";
import "./control-points.css";
import { usePageTitle } from "../../hooks";

/**
 * ControlPointList Component
 * Displays a list of all control points with local filtering, search, and sorting capabilities
 */
export const ControlPointList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [photoGalleryVisible, setPhotoGalleryVisible] = useState(false);
  const [selectedControlPoint, setSelectedControlPoint] = useState<{ id: string; name: string } | null>(null);
  
  // Set page title
  usePageTitle(t("controlPoint.title", "Puntos de Control"));

  const { tableProps } = useTable<ControlPoint>({
    resource: "control-points",
    pagination: {
      mode: "client",
      pageSize: 15,
    },
    syncWithLocation: false,
  });

  const handleViewDetail = (record: ControlPoint) => {
    navigate(`/control-points/${record.id}`, {
      state: { from: "/control-points" },
    });
  };

  const handleViewPhotos = (id: string, name: string) => {
    setSelectedControlPoint({ id, name });
    setPhotoGalleryVisible(true);
  };

  // Get status color
  const getStatusColor = (status: ControlPointStatus): string => {
    return status === "open" ? "green" : "red";
  };

  // Get status text
  const getStatusText = (status: ControlPointStatus): string => {
    return status === "open" 
      ? t("controlPoint.status.open", "Abierto") 
      : t("controlPoint.status.closed", "Cerrado");
  };

  // Filter data locally based on search text
  const filteredData = useMemo(() => {
    const data = (tableProps.dataSource || []) as ControlPoint[];
    
    if (!searchText || searchText.trim() === "") {
      return data;
    }

    const searchLower = searchText.toLowerCase().trim();
    
    return data.filter((record) => {
      const producerName = record.producer_name?.toLowerCase() || "";
      const farmName = record.farm_name?.toLowerCase() || "";
      const municipality = record.farm_municipality?.toLowerCase() || "";
      const status = getStatusText(record.status).toLowerCase();
      
      return (
        producerName.includes(searchLower) ||
        farmName.includes(searchLower) ||
        municipality.includes(searchLower) ||
        status.includes(searchLower)
      );
    });
  }, [tableProps.dataSource, searchText]);

  // Clear search
  const handleClearSearch = () => {
    setSearchText("");
  };



  return (
    <div className="control-points-list">
      <List>
        {/* Search Section */}
        <Space style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
          <Input.Search
            placeholder={t("controlPoint.search", "Buscar por productor, finca, municipio, estado...")}
            allowClear
            enterButton={<SearchOutlined />}
            size="middle"
            style={{ width: 450 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearSearch}
              type="default"
            >
              {t("common.clearSearch", "Limpiar búsqueda")}
            </Button>
          )}
        </Space>

        {/* Table */}
        <Table 
          {...tableProps}
          dataSource={filteredData}
          rowKey="id" 
          scroll={{ x: 1400 }}
        >
          <Table.Column
            dataIndex="producer_name"
            title={t("controlPoint.producer", "Productor")}
            width={200}
            sorter={(a: ControlPoint, b: ControlPoint) => 
              (a.producer_name || "").localeCompare(b.producer_name || "")
            }
          />
          <Table.Column
            dataIndex="farm_name"
            title={t("controlPoint.farm", "Finca")}
            width={200}
            sorter={(a: ControlPoint, b: ControlPoint) => 
              (a.farm_name || "").localeCompare(b.farm_name || "")
            }
          />
          <Table.Column
            dataIndex="farm_municipality"
            title={t("controlPoint.municipality", "Municipio (Provincia)")}
            width={180}
            sorter={(a: ControlPoint, b: ControlPoint) => 
              (a.farm_municipality || "").localeCompare(b.farm_municipality || "")
            }
          />
          <Table.Column
            dataIndex="detection_date"
            title={t("controlPoint.detectionDate", "Fecha Detección")}
            width={140}
            defaultSortOrder="descend"
            sorter={(a: ControlPoint, b: ControlPoint) => {
              const dateA = a.detection_date ? new Date(a.detection_date).getTime() : 0;
              const dateB = b.detection_date ? new Date(b.detection_date).getTime() : 0;
              return dateA - dateB;
            }}
            render={(value: string) => value ? dayjs(value).format("DD/MM/YYYY") : '-'}
          />
          <Table.Column
            dataIndex="status"
            title={t("controlPoint.status.title", "Estado")}
            width={100}
            sorter={(a: ControlPoint, b: ControlPoint) => 
              a.status.localeCompare(b.status)
            }
            render={(value: ControlPointStatus) => (
              <Tag color={getStatusColor(value)}>
                {getStatusText(value)}
              </Tag>
            )}
          />
          <Table.Column
            dataIndex="total_estimated_surface"
            title={t("controlPoint.estimatedSurface", "Superficie (ha)")}
            width={130}
            align="center"
            sorter={(a: ControlPoint, b: ControlPoint) => 
              (a.total_estimated_surface || 0) - (b.total_estimated_surface || 0)
            }
            render={(value: number) => value ? value.toFixed(2) : '-'}
          />
          <Table.Column
            dataIndex="review_count"
            title={t("controlPoint.reviewCount", "Revisiones")}
            width={100}
            align="center"
            sorter={(a: ControlPoint, b: ControlPoint) => 
              a.review_count - b.review_count
            }
          />
          <Table.Column
            dataIndex="sample_count"
            title={t("controlPoint.sampleCount", "Nº Muestras")}
            width={110}
            align="center"
            sorter={(a: ControlPoint, b: ControlPoint) => 
              a.sample_count - b.sample_count
            }
          />
          <Table.Column
            dataIndex="photo_count"
            title={t("controlPoint.photos", "Fotos")}
            width={90}
            align="center"
            sorter={(a: ControlPoint, b: ControlPoint) => 
              a.photo_count - b.photo_count
            }
            render={(count: number, record: ControlPoint) => (
              <Button
                type={count > 0 ? "link" : "default"}
                size="small"
                icon={<PictureOutlined />}
                onClick={() => handleViewPhotos(record.id, record.name)}
                disabled={count === 0}
              >
                {count}
              </Button>
            )}
          />
          <Table.Column
            title={t("common.actions", "Acciones")}
            dataIndex="actions"
            width={100}
            fixed="right"
            render={(_, record: ControlPoint) => (
              <Space>
                <ShowButton
                  hideText
                  size="small"
                  recordItemId={record.id}
                  onClick={() => handleViewDetail(record)}
                />
              </Space>
            )}
          />
        </Table>

        {/* Photo Gallery Modal */}
        {selectedControlPoint && (
          <PhotoGalleryModal
            visible={photoGalleryVisible}
            onClose={() => {
              setPhotoGalleryVisible(false);
              setSelectedControlPoint(null);
            }}
            entityId={selectedControlPoint.id}
            entityName={selectedControlPoint.name}
            entityType="controlPoint"
          />
        )}
      </List>
    </div>
  );
};
